import { useCallback, useEffect, useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUpdateIdentity } from '@Pages/happyFlowPages/identity/hooks/useUpdateIdentity.tsx';
import {
  clearCustomerDataOnSubmit,
  getIdentityDefaultValues,
  syncSliceWithNonUsFormData,
  syncSliceWithUsFormData,
} from '@Pages/happyFlowPages/identity/identity.service.ts';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useFormHook, useSetError } from '@Hooks/index';
import { IdentityFormTypes } from '@Components/Identity/IdentityFormTypeSwitcher.tsx';
import { useDynamicIdentityScheme } from '@Lib/Identity/scheme/useDynamicIdentityScheme.ts';
import { OnboardingFormFields } from '@Types/types.ts';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { RoutesMap } from '@Router/router.types.ts';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types';
import { setPeopleProperty } from '@Services/analytics/tracking.service';
import { eddAllRoutes, cddRoutes } from '@Consts/navigation-stack-map';
import { applyCountryEddToNavigationStack } from '@Services/init/util';
import { routesModel } from '@Router/router.consts';
import type { CountryApiResponse } from '@Types/country.ts';
import type { IIdentityForm, IIdentityUsForm } from '@Lib/Identity/types.ts';

export const useIdentityLogic = (originRoute: RoutesMap = RoutesMap.IDENTITY) => {
  const { onboardingFlowIdentifier } = useBoundStore.getState().onboardingSlice;
  const isAuthUser = onboardingFlowIdentifier === OnboardingFlowIdentifier.AUTHORIZED_USER;
  const isPhoneCountryCodeUs = useBoundStore.getState().isPhoneCountryCodeUs();
  const routeApi = getRouteApi(routesModel[originRoute].path);
  const countryData = routeApi.useLoaderData() as CountryApiResponse;
  const isNonUsSupported = isAuthUser ? true : useBoundStore.getState().customer.isNonUsSupported;
  const editMode = isAuthUser ? false : useBoundStore.getState().onboardingSlice.editMode;
  const isUsFlow = isAuthUser
    ? useBoundStore.getState().getAuthUserIsUsFlow()
    : useBoundStore.getState().getIsUsFlow();

  const {
    navigateToNextPage,
    removeNavStackItemByPageName,
    addNavStackItemAfterSpecificPage,
    addNavStackListItemsAfterSpecificPage,
  } = useNavigationManager();

  const [formType, setFormType] = useState<IdentityFormTypes>(
    !isUsFlow
      ? IdentityFormTypes.nonUs
      : isPhoneCountryCodeUs
        ? IdentityFormTypes.us
        : IdentityFormTypes.nonUs
  );

  const { scheme } = useDynamicIdentityScheme(countryData?.countries || [], formType, undefined);

  const formMethods = useFormHook({
    formSchema: scheme,
    formDefaultValues: getIdentityDefaultValues(formType) as IIdentityForm,
    schemaTitle: `${formType}_Form`,
  });

  const reactiveNationality = formMethods.watch(OnboardingFormFields.NATIONALITY);
  const reactiveFormType = formMethods.watch('formType');

  const selectedCountry = countryData?.countries?.find(
    (country) => country.code === reactiveNationality
  );

  const handleSchemeChangeEffect = () => {
    formMethods.clearErrors();
    const formValues = formMethods.getValues();

    for (const key in formValues) {
      if (formValues[key as keyof IIdentityForm]) {
        formMethods.trigger(key as keyof IIdentityForm);
      }
    }
  };

  useEffect(() => {
    if (formMethods.getValues(OnboardingFormFields.DATE_OF_BIRTH)) {
      formMethods.trigger(OnboardingFormFields.DATE_OF_BIRTH);
    }
  }, [selectedCountry]);

  useEffect(() => {
    handleSchemeChangeEffect();
  }, [reactiveFormType]);

  const updateNavStackByFormType = () => {
    const getOnboardingData = useBoundStore.getState().onboardingSlice.getOnboardingData;
    const setPickupWhereLeftNavigationStack =
      useBoundStore.getState().onboardingSlice.setPickupWhereLeftNavigationStack;

    if (formType === IdentityFormTypes.nonUs) {
      /** Add nonUS routes */
      addNavStackItemAfterSpecificPage(RoutesMap.BUSINESS_ADDRESS, RoutesMap.MAILING_ADDRESS);
      addNavStackListItemsAfterSpecificPage(RoutesMap.OWNER_CENTER, eddAllRoutes);
      addNavStackItemAfterSpecificPage(RoutesMap.REVIEW, RoutesMap.NON_US_SOCURE);
      removeNavStackItemByPageName(RoutesMap.SUCCESS);
      applyCountryEddToNavigationStack();
    } else if (formType === IdentityFormTypes.us) {
      /** Clear nonUS routes, but keep CDD and EDD_INTENDED_USE which are part of US flows */
      addNavStackItemAfterSpecificPage(RoutesMap.REVIEW, RoutesMap.SUCCESS);
      removeNavStackItemByPageName(RoutesMap.NON_US_SOCURE);
      removeNavStackItemByPageName(RoutesMap.MAILING_ADDRESS);
      const routesToKeepForUs = [...cddRoutes, RoutesMap.EDD_INTENDED_USE];
      eddAllRoutes
        .filter((route) => !routesToKeepForUs.includes(route))
        .forEach((route) => {
          removeNavStackItemByPageName(route);
        });
      applyCountryEddToNavigationStack();
    }

    if (getOnboardingData().navigationStack.length > 0) {
      setPickupWhereLeftNavigationStack(getOnboardingData().navigationStack);
    }
  };

  const { mutateAsync: asyncUpdateIdentity, isPending } = useUpdateIdentity(() => {
    if (formType === IdentityFormTypes.us) {
      syncSliceWithUsFormData(formMethods.getValues() as IIdentityUsForm);
    } else if (formType === IdentityFormTypes.nonUs) {
      syncSliceWithNonUsFormData(formMethods.getValues() as IIdentityForm);
    }

    if (isAuthUser) {
      useBoundStore.getState().setAuthUserIsUsFlow(formType === IdentityFormTypes.us);
    } else {
      setPeopleProperty({ isNonUs: formType === IdentityFormTypes.nonUs });
      updateNavStackByFormType();
    }

    navigateToNextPage(originRoute);
  });

  const handleSubmit = useCallback(async () => {
    clearCustomerDataOnSubmit(formType);
    const formData = formMethods.getValues();
    if (formType === IdentityFormTypes.us) {
      formMethods.setValue(OnboardingFormFields.NATIONALITY, 'US');
    }
    await asyncUpdateIdentity({
      formType,
      props: formData,
    });
  }, [asyncUpdateIdentity, formMethods]);
  useSetError({ formMethods });

  const handleFormTypeChange = (formTypeProp: IdentityFormTypes) => {
    setFormType(formTypeProp);
    formMethods.setValue('formType', formTypeProp);
  };

  const countryDataList =
    countryData?.countries.map((country) => ({
      title: country.name,
      value: country.code,
    })) || [];

  return {
    isPending,
    handleSubmit,
    formMethods,
    formType,
    handleFormTypeChange,
    supportedCountries: countryData,
    countryDataList,
    selectedCountry,
    isNonUsSupported,
    isPhoneCountryCodeUs,
    editMode,
  };
};
